import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Check, Palette } from 'lucide-react';
import styled from 'styled-components';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background-color: ${props => props.theme.colors.background.card};
  border-top: 1px solid ${props => props.theme.colors.border.main};
  border-bottom: 1px solid ${props => props.theme.colors.border.main};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
`;

const ContentWrapper = styled.div`
  max-width: 64rem;
  margin: 0 auto;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: row;
  }
`;

const IconTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const IconWrapper = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary.main}1A;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${props => props.theme.colors.primary.main};
  }
`;

const TitleSection = styled.div`
  h3 {
    font-family: ${props => props.theme.typography.fontFamily.display};
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
    margin: 0;
  }
  
  p {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.muted};
    margin: 0;
  }
`;

const ColorSwatches = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    justify-content: flex-start;
  }
`;

const ColorButton = styled.button`
  position: relative;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 2px solid ${props => props.isSelected 
    ? props.theme.colors.text.primary 
    : 'rgba(255, 255, 255, 0.5)'};
  background-color: ${props => props.color};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    transform: scale(1.1);
  }
  
  ${props => props.isSelected && `
    box-shadow: 0 0 0 2px ${props.theme.colors.text.primary}33;
  `}
  
  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => {
      const darkColors = ['#000080', '#191970', '#4169E1', '#4682B4'];
      return darkColors.includes(props.color) ? 'white' : props.theme.colors.text.primary;
    }};
  }
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: -2rem;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: ${props => props.theme.typography.fontSize.xs};
  background-color: ${props => props.theme.colors.text.primary};
  color: ${props => props.theme.colors.background.card};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  opacity: 0;
  pointer-events: none;
  transition: ${props => props.theme.transitions.default};
  
  ${ColorButton}:hover & {
    opacity: 1;
  }
`;

const SelectedColor = styled.div`
  margin-top: ${props => props.theme.spacing['2xl']};
  text-align: center;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    text-align: right;
  }
  
  span:first-child {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.muted};
  }
  
  span:last-child {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const ColorOptionsSection = () => {
  const { t } = useLanguage();
  const [selectedColor, setSelectedColor] = useState(0);

  const colors = [
    { name: t('colors.skyBlue'), hex: '#87CEEB', class: 'bg-[#87CEEB]' },
    { name: t('colors.powderBlue'), hex: '#B0E0E6', class: 'bg-[#B0E0E6]' },
    { name: t('colors.babyBlue'), hex: '#89CFF0', class: 'bg-[#89CFF0]' },
    { name: t('colors.royalBlue'), hex: '#4169E1', class: 'bg-[#4169E1]' },
    { name: t('colors.cornflowerBlue'), hex: '#6495ED', class: 'bg-[#6495ED]' },
    { name: t('colors.steelBlue'), hex: '#4682B4', class: 'bg-[#4682B4]' },
    { name: t('colors.navyBlue'), hex: '#000080', class: 'bg-[#000080]' },
    { name: t('colors.midnightBlue'), hex: '#191970', class: 'bg-[#191970]' },
  ];

  return (
    <Section>
      <Container>
        <ContentWrapper>
          <FlexContainer>
            <IconTitleSection>
              <IconWrapper>
                <Palette />
              </IconWrapper>
              <TitleSection>
                <h3>{t('colors.title')}</h3>
                <p>{t('colors.subtitle')}</p>
              </TitleSection>
            </IconTitleSection>

            <ColorSwatches>
              {colors.map((color, index) => (
                <ColorButton
                  key={index}
                  color={color.hex}
                  isSelected={selectedColor === index}
                  onClick={() => setSelectedColor(index)}
                  title={color.name}
                >
                  {selectedColor === index && <Check />}
                  <Tooltip>{color.name}</Tooltip>
                </ColorButton>
              ))}
            </ColorSwatches>
          </FlexContainer>

          <SelectedColor>
            <span>{t('colors.selected')}: </span>
            <span>{colors[selectedColor].name}</span>
          </SelectedColor>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default ColorOptionsSection;
